<script setup lang="ts">
import { orderPayByTrackCreate } from "@innoclouds/api";
import { useTranslation } from "i18next-vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import IconHandBand from "#/assets/icons/hand_band.svg";
import { useHidScanner } from "#/composables/useHidScanner";
import { useThemeStore } from "#/store/theme";
import Loader from "#/ui/loader/Loader.vue";

interface Props {
	orderId?: string;
}

interface Emits {
	cancel: [];
}

const PAYMENT_TRACKER_TIMEOUT = 10;

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useTranslation();

const themeStore = useThemeStore();

let timer: ReturnType<typeof setInterval> | undefined;
const timeLeft = ref(PAYMENT_TRACKER_TIMEOUT);
const isPendingPayment = ref(false);
const errorMessage = ref<string | null>(null);

const title = computed(() => themeStore.data?.kiosk_payment_tracker_title || t(`cart-complete.payment-tracker.title`));
const subtitle = computed(() => themeStore.data?.kiosk_payment_tracker_description || t(`cart-complete.payment-tracker.subtitle`));
const icon = computed(() => themeStore.data?.kiosk_payment_tracker_image || IconHandBand);

const formattedTime = computed(() => {
	const left = Math.max(timeLeft.value, 0);
	const minutes = Math.floor(left / 60);
	const seconds = left % 60;
	return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
});

const cancelPayment = () => {
	clearInterval(timer);
	isPendingPayment.value = false;
	errorMessage.value = null;
	emit("cancel");
};

const retryPayment = () => {
	clearInterval(timer);
	isPendingPayment.value = false;
	errorMessage.value = null;
	startTimer();
};

const updateTimer = () => {
	timeLeft.value--;
	if (timeLeft.value <= 0) {
		clearInterval(timer);
		errorMessage.value = t(`cart-complete.payment-tracker.error.timeout`);
	}
};

const startTimer = () => {
	timeLeft.value = PAYMENT_TRACKER_TIMEOUT;
	timer = setInterval(updateTimer, 1000);
};

const onScanPaymentTracker = async (code: string) => {
	if (isPendingPayment.value || errorMessage.value) return;
	isPendingPayment.value = true;
	clearInterval(timer);
	try {
		if (!props.orderId) throw new Error("Order ID is required");
		await orderPayByTrackCreate(props.orderId, { track_number: code });
	} catch (error: any) {
		errorMessage.value = error.response?.data?.user_friendly_message || t(`cart-complete.payment-tracker.error`);
		console.error("onScanPaymentTracker error", error);
		isPendingPayment.value = false;
	}
};

const { onKeyDown } = useHidScanner({ onScan: onScanPaymentTracker, minLength: 1, maxKeyInterval: 90, registerListener: false });

const _onKeyDown = (e: KeyboardEvent) => {
	if (isPendingPayment.value || errorMessage.value) return;
	onKeyDown(e);
};

onMounted(() => {
	window.addEventListener("keydown", _onKeyDown);
	startTimer();
});

onBeforeUnmount(() => {
	clearInterval(timer);
	window.removeEventListener("keydown", _onKeyDown);
});
</script>

<template>
	<div class="payment_tracker_overlay">
		<div class="payment_tracker_panel">
			<span class="payment_tracker_grip" />

			<Transition name="fade" mode="out-in">
				<div class="payment_tracker_content" v-if="errorMessage">
					<div class="payment_tracker_stage" aria-invalid="true">
						<div class="payment_tracker_error"></div>
					</div>

					<h1 class="payment_tracker_title">{{ t(`cart-complete.payment-tracker.title.error`) }}</h1>
					<p class="payment_tracker_subtitle">{{ errorMessage }}</p>

					<div class="payment_tracker_buttons">
						<button type="button" class="payment_tracker_button payment_tracker_retry" @click="retryPayment">
							{{ t(`cart-complete.payment-tracker.button.retry`) }}
						</button>
						<button type="button" class="payment_tracker_button payment_tracker_cancel" @click="cancelPayment">
							{{ t(`cart-complete.payment-tracker.button.cancel`) }}
						</button>
					</div>
				</div>
				<div class="payment_tracker_content" v-else-if="isPendingPayment">
					<div class="payment_tracker_stage" aria-hidden="true">
						<span class="payment_tracker_wave" />
						<span class="payment_tracker_wave" />
						<Loader size="60px" />
					</div>
				</div>
				<div class="payment_tracker_content" v-else>
					<h1 class="payment_tracker_title">{{ title }}</h1>
					<p class="payment_tracker_subtitle">{{ subtitle }}</p>

					<div class="payment_tracker_stage" aria-hidden="true">
						<span class="payment_tracker_wave" />
						<span class="payment_tracker_wave" />
						<img class="payment_tracker_icon" :src="icon" alt="">
					</div>

					<p class="payment_tracker_timer">{{ formattedTime }}</p>
					<p class="payment_tracker_waiting">{{ t(`cart-complete.payment-tracker.waiting`) }}</p>

					<div class="payment_tracker_buttons">
						<button type="button" class="payment_tracker_button payment_tracker_cancel" @click="cancelPayment">
							{{ t(`cart-complete.payment-tracker.button.cancel`) }}
						</button>
					</div>
				</div>
			</Transition>
		</div>
	</div>
</template>

<style lang="scss" scoped>
  @use './cartCompletePaymentTracker.scss';
</style>
